import { useEffect, useState } from "react";
import { Phone, MessageSquare, CalendarCheck, Download, Tags } from "lucide-react";
import { toast } from "sonner";
import { adminApi, downloadCsv } from "@/lib/adminApi";

const RANGES = [7, 30, 90];
const KINDS = [
  { key: "call", label: "Call taps", Icon: Phone },
  { key: "text", label: "Text taps", Icon: MessageSquare },
  { key: "booking", label: "Booking clicks", Icon: CalendarCheck },
  { key: "package", label: "Package requests", Icon: Tags },
];
const TIER_LABEL = { essentials: "Essentials · $250", standard: "Standard · $400" };
const ICON = Object.fromEntries(KINDS.map((k) => [k.key, k.Icon]));

const fmt = (iso) =>
  new Date(iso).toLocaleString("en-US", { timeZone: "America/Phoenix", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export const ClickStats = ({ onAuthError }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    adminApi.get(`/admin/clicks?days=${days}`).then((r) => setData(r.data)).catch((e) => e?.response?.status === 401 && onAuthError());
  }, [days, onAuthError]);

  return (
    <section data-testid="admin-click-stats" className="bg-cream-ivory border border-[#D9CFB8] p-8 lg:p-10">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8a6b2f] mb-2">Click tracking</p>
          <h2 className="font-serif text-3xl text-navy">Calls, texts & bookings</h2>
        </div>
        <div className="flex flex-wrap gap-2" role="tablist">
          {RANGES.map((r) => (
            <button key={r} onClick={() => setDays(r)} data-testid={`clicks-range-${r}`}
              className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${days === r ? "bg-navy text-parchment border-navy" : "border-[#C9BFA8] text-ink-muted hover:text-navy"}`}>
              {r} days
            </button>
          ))}
          <button data-testid="clicks-export-btn"
            onClick={() => downloadCsv(`/admin/export/clicks.csv?days=${days}`, `oakford-clicks-${days}d.csv`).catch(() => toast.error("Export failed."))}
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-[#8a6b2f] text-[#8a6b2f] hover:bg-[#8a6b2f] hover:text-cream transition-colors">
            <Download size={12} /> CSV
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
        {KINDS.map(({ key, label, Icon }) => (
          <div key={key} className="bg-navy text-parchment p-6">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">{label}</p>
              <Icon size={16} className="text-gold" />
            </div>
            <p data-testid={`clicks-total-${key}`} className="font-serif text-5xl mt-3">{data ? data.totals[key] : "—"}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-parchment-muted mt-2">
              Last {days} days · <span data-testid={`clicks-all-time-${key}`}>{data ? data.all_time[key] : "—"}</span> all time
            </p>
          </div>
        ))}
      </div>

      {data?.by_package && (
        <div data-testid="package-interest" className="mt-10 border border-[#D9CFB8] p-6">
          <div className="flex items-center justify-between gap-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-ink">Package interest · "Request a consultation" clicks by tier</p>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Last {days} days</span>
          </div>
          {(() => {
            const max = Math.max(1, ...data.by_package.map((p) => p.count));
            const total = data.by_package.reduce((s, p) => s + p.count, 0);
            const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
            return (
              <ul className="mt-5 space-y-5">
                {data.by_package.map((p) => (
                  <li key={p.tier} data-testid={`package-interest-${p.tier}`} className="grid grid-cols-12 items-center gap-4">
                    <span className="col-span-4 sm:col-span-3 font-mono text-[11px] uppercase tracking-[0.18em] text-navy">{TIER_LABEL[p.tier]}</span>
                    <span className="col-span-5 sm:col-span-6 h-2 bg-cream relative overflow-hidden">
                      <span className="absolute inset-y-0 left-0 bg-[#8a6b2f] transition-[width] duration-500" style={{ width: `${(p.count / max) * 100}%` }} />
                    </span>
                    <span className="col-span-3 text-right text-sm text-navy leading-tight">
                      <span data-testid={`package-interest-${p.tier}-count`} className="font-serif text-xl">{p.count}</span>
                      <span className="text-ink-muted text-xs"> clicks · {pct(p.count, total)}%</span>
                      <span className="block text-xs text-ink-muted">
                        → <span data-testid={`package-interest-${p.tier}-inquiries`} className="text-navy">{p.inquiries}</span> inquir{p.inquiries === 1 ? "y" : "ies"}
                        <span data-testid={`package-interest-${p.tier}-conversion`}> ({pct(p.inquiries, p.count)}%)</span> · {p.all_time} / {p.inquiries_all_time} all time
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            );
          })()}
          <p className="mt-5 text-xs text-ink-muted">Conversion = inquiries naming that package ÷ "Request a consultation" clicks on that tier, within the selected range.</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-10 mt-10">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-ink mb-4">By page</p>
          {data?.by_page?.length ? (
            <table data-testid="clicks-by-page" className="w-full text-sm text-navy">
              <thead>
                <tr className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted">
                  <th className="text-left font-normal pb-2">Page</th>
                  <th className="text-right font-normal pb-2">Calls</th>
                  <th className="text-right font-normal pb-2">Texts</th>
                  <th className="text-right font-normal pb-2">Bookings</th>
                  <th className="text-right font-normal pb-2">Packages</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#D9CFB8]">
                {data.by_page.map((p) => (
                  <tr key={p.page}>
                    <td className="font-mono py-3">{p.page}</td>
                    <td className="text-right font-serif text-lg py-3">{p.call}</td>
                    <td className="text-right font-serif text-lg py-3">{p.text}</td>
                    <td className="text-right font-serif text-lg py-3">{p.booking}</td>
                    <td className="text-right font-serif text-lg py-3">{p.package}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="text-ink-muted text-sm">No clicks recorded in this range yet.</p>}
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-ink mb-4">Recent (Arizona time)</p>
          {data?.recent?.length ? (
            <ul data-testid="clicks-recent" className="divide-y divide-[#D9CFB8] max-h-72 overflow-y-auto pr-2">
              {data.recent.map((c) => {
                const Icon = ICON[c.kind];
                return (
                  <li key={c.id} className="flex items-center gap-3 py-3 text-sm text-navy">
                    <Icon size={13} className="text-[#8a6b2f] shrink-0" />
                    <span className="font-mono">{c.page}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-muted">{c.placement}</span>
                    <span className="text-ink-muted ml-auto">{fmt(c.created_at)}</span>
                  </li>
                );
              })}
            </ul>
          ) : <p className="text-ink-muted text-sm">Nothing yet.</p>}
        </div>
      </div>
    </section>
  );
};
